import React from "react";
import { Award } from "lucide-react";

/**
 * Reputation panel — small bar showing current reputation (0.0–0.10) and
 * how it boosts the player's effective suggestion influence.
 *
 * Rep grows from objectives, leaderboard claims, custom-tactic "critical"
 * verdicts. It caps at 10% on top of the rank's base suggestion_weight.
 */
export default function ReputationPanel({ state }) {
  if (!state) return null;

  const rep = parseFloat(state.reputation || 0);
  const baseW = state.rank?.suggestion_weight || 0;
  const effW = state.effective_suggestion_weight || baseW;
  const pct = Math.min(100, Math.max(2, Math.round((rep / 0.10) * 100)));
  const repPctText = (rep * 100).toFixed(1);
  const baseText = Math.round(baseW * 100);
  const effText = Math.round(effW * 100);

  return (
    <div className="tactical-card" data-testid="reputation-panel">
      <div className="flex items-baseline justify-between mb-2">
        <span className="tac-label flex items-center gap-2">
          <Award className="w-3 h-3 text-blood" /> REPUTATION
        </span>
        <span className="font-mono text-[0.65rem] text-muted-h tabular-nums"
              data-testid="reputation-value">
          +{repPctText}% / +10.0% MAX
        </span>
      </div>
      <div className="bar-track">
        <div className="bar-fill"
             data-testid="reputation-bar"
             style={{
               width: `${pct}%`,
               background: rep >= 0.08
                            ? "var(--bone)"
                            : rep > 0.0
                            ? "linear-gradient(90deg, var(--blood), #c4b88a)"
                            : "var(--blood)",
             }} />
      </div>
      <p className="text-[0.65rem] text-muted-h mt-2 leading-relaxed">
        Influence over your superior:
        {" "}<span className="font-mono text-bone">{baseText}%</span>
        {effText !== baseText && (
          <> → <span className="font-mono text-blood" data-testid="reputation-effective">{effText}%</span></>
        )}
        . Rises from completed objectives, leaderboard claims, and clever improvised tactics.
      </p>
    </div>
  );
}
